"use client"

import {
  FetchModalsAndAboutDocument,
  FetchModalsAndAboutQuery,
  PortfolioStaticDataQuery,
  PortfolioStaticFiltersQuery,
} from "@/gql/graphql"
import { Modals } from "./components/Modals"
import { PortfolioSearchParams, PortfolioUIState } from "./page"
import { FragmentType, graphql, useFragment } from "@/gql"
import { PortfolioHeaderDesktop } from "./components/PortfolioHeaderDesktop"
import { PortfolioHeaderMobile } from "./components/PortfolioHeaderMobile"
import { PortfolioProjects } from "./components/PortfolioProjects"
import { PageMaxWidth } from "@/components"
import { StickyHeader } from "./components/StickyHeader"
import { useEffect, useState } from "react"
import clsx from "clsx"
import { AboutContent } from "./components/AboutContent"
import { PrivatePortfolioSection } from "./components/PrivatePortfolioSection"
import type { NovaSession } from "@/types"
import { Separator, Spinner } from "@/elements"
import { TabsWithFilterButton } from "./components/TabsWithFilterButton"
import { useQuery } from "@apollo/client"
import { EditModals } from "./components/edit/EditModals"
import { useEditPortfolio } from "./components/edit/useEditPortfolio"

export const PortfolioClientPageFragment = graphql(`
  fragment PortfolioClientPage on IndividualCreative {
    __typename
    id
    name
    bio
    publicVisibility
    displayName
    user {
      id
      firstName
      name
    }

    portfolioClients {
      edges {
        node {
          id
        }
      }
    }

    ...PortfolioHeader
    ...PortfolioProjects
    ...PortfolioStickyHeader
  }
`)

graphql(`
  query FetchModalsAndAbout($id: ID!, $displayingPrivateProfile: Boolean!, $isCreativeViewer: Boolean!) {
    individualCreative(id: $id) {
      ...PortfolioModals
      ...AboutContent
      ...EditModals
    }
    currentUser {
      id
      ...AboutContentIsPro
    }
  }
`)

interface Props {
  clientCreative: FragmentType<typeof PortfolioClientPageFragment> | undefined | null
  serverCreative: FragmentType<typeof PortfolioClientPageFragment> | undefined | null
  params: { handle: string }
  searchParams: PortfolioSearchParams
  uiState: PortfolioUIState
  serverSession: NovaSession | null
  staticData: PortfolioStaticDataQuery | null | undefined
  fetchMoreClientSide?: any
  isFetchingFreshClientSideData: boolean
  editProfileMethods?: ReturnType<typeof useEditPortfolio>
}

const validTabs = ["work", "about"]

export const PortfolioClientPage: React.FC<Props> = props => {
  const {
    params,
    uiState,
    serverSession,
    staticData,
    fetchMoreClientSide,
    searchParams,
    isFetchingFreshClientSideData,
    editProfileMethods,
  } = props
  const defaultTab = validTabs.includes(searchParams.tab) ? searchParams.tab : "work"
  const [activeTab, setActiveTab] = useState<"work" | "about">(defaultTab)
  const clientCreative = useFragment(PortfolioClientPageFragment, props.clientCreative)
  const serverCreative = useFragment(PortfolioClientPageFragment, props.serverCreative)
  const [showStickyHeader, setShowStickyHeader] = useState(false)

  const isEditing = editProfileMethods?.isEditing

  const handle = params.handle

  const creative = clientCreative || serverCreative

  const [isLoadingAbout, setIsLoadingAbout] = useState(false)
  const { data: modalsDataApollo, loading: modalsDataLoading } = useQuery(FetchModalsAndAboutDocument, {
    variables: {
      id: creative?.id!,
      displayingPrivateProfile: !(creative?.publicVisibility === "PUBLIC" || !!serverSession?.user?.token),
      isCreativeViewer: serverSession?.account === "creative",
    },
    skip: !creative?.id || !uiState.isAuthenticated,
  })

  const [unauthedModalData, setUnauthedModalData] = useState<FetchModalsAndAboutQuery | null>(null)
  const data = modalsDataApollo || unauthedModalData

  useEffect(() => {
    if (uiState.isAuthenticated) return
    const fetchPortfolioFilters = async () => {
      setIsLoadingAbout(true)
      const res = await fetch(`/api/portfolio/modalsAndAbout?creativeId=${staticData?.creativeByHandle.id}`)
      const data: { data: FetchModalsAndAboutQuery | null } = await res.json()
      setUnauthedModalData(data?.data)
      setIsLoadingAbout(false)
    }
    fetchPortfolioFilters()
  }, [staticData?.creativeByHandle.id, uiState.isAuthenticated])

  useEffect(() => {
    setIsLoadingAbout(modalsDataLoading)
  }, [modalsDataLoading])

  useEffect(() => {
    const pageScrollWrapper = document.getElementById("page-scroll-wrapper")
    const handleScroll = () => {
      const mainHeader = document.getElementById("main-header")
      if (mainHeader) {
        const headerBottom = mainHeader.getBoundingClientRect().bottom
        setShowStickyHeader(headerBottom <= 0)
      }
    }

    pageScrollWrapper?.addEventListener("scroll", handleScroll)
    return () => pageScrollWrapper?.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <>
      {isEditing && (
        <EditModals
          uiState={uiState}
          editProfileMethods={editProfileMethods}
          creative={modalsDataApollo?.individualCreative}
          searchParams={searchParams}
        />
      )}

      <div className="relative min-h-screen w-full">
        {!isEditing && (
          <div className="sticky top-0 z-50 hidden w-full bg-bg-1 sm:flex">
            <div
              className={clsx(
                "absolute left-0 right-0 top-0 transition-[opacity,transform] duration-200 ease-in-out",
                showStickyHeader ? "h-auto translate-y-0 opacity-100" : "h-0 translate-y-[-120px] opacity-100"
              )}>
              <PageMaxWidth className="px-4 sm:px-[48px] xl:px-[96px]">
                <StickyHeader creative={creative} serverSession={serverSession} uiState={uiState} />
              </PageMaxWidth>
            </div>
          </div>
        )}
        <PageMaxWidth className="px-4 pb-5 sm:px-[48px] xl:px-[96px]">
          <div className="relative w-full">
            {uiState.isPublic && (
              <Modals
                creative={data?.individualCreative}
                uiState={uiState}
                serverSession={serverSession}
                editProfileMethods={editProfileMethods}
                viewerUser={data?.currentUser}
              />
            )}
            <div id="main-header">
              <div className="mb-9 mt-8 hidden md:flex md:flex-col">
                <PortfolioHeaderDesktop
                  creative={creative}
                  size="lg"
                  uiState={uiState}
                  serverSession={serverSession}
                  editProfileMethods={editProfileMethods}
                />
              </div>
              <div className="md:hidden">
                <PortfolioHeaderMobile
                  creative={creative}
                  uiState={uiState}
                  serverSession={serverSession}
                  editProfileMethods={editProfileMethods}
                />
              </div>
            </div>
            {uiState.isPublic && uiState.hasProjects && (
              <TabsWithFilterButton
                activeTab={activeTab}
                onChangeTab={setActiveTab}
                staticData={staticData}
                uiState={uiState}
                serverSession={serverSession}
                editProfileMethods={editProfileMethods}
              />
            )}
            <div className={uiState.isPublic && uiState.hasProjects ? "py-6" : "pb-6"}>
              <Separator />
            </div>
            {!uiState.isPublic && <PrivatePortfolioSection />}
            {uiState.hasProjects && uiState.isPublic && activeTab === "work" && (
              <PortfolioProjects
                clientCreative={clientCreative}
                serverCreative={serverCreative}
                handle={handle}
                uiState={uiState}
                staticData={staticData}
                serverSession={serverSession}
                fetchMoreClientSide={fetchMoreClientSide}
                isFetchingFreshClientSideData={isFetchingFreshClientSideData}
                editProfileMethods={editProfileMethods}
              />
            )}
            {uiState.hasProjects && uiState.isPublic && activeTab === "about" && (
              <div className="">
                {isLoadingAbout ? (
                  <div className="relative flex h-[300px] w-full items-center justify-center">
                    <Spinner />
                  </div>
                ) : (
                  <AboutContent
                    creative={data?.individualCreative}
                    serverSession={serverSession}
                    uiState={uiState}
                    editProfileMethods={editProfileMethods}
                    viewerUser={data?.currentUser}
                  />
                )}
              </div>
            )}
            {!uiState.hasProjects && uiState.isPublic && (
              <div className="mb-5">
                {isLoadingAbout ? (
                  <div className="relative flex h-[300px] w-full items-center justify-center">
                    <Spinner />
                  </div>
                ) : (
                  <AboutContent
                    creative={data?.individualCreative}
                    serverSession={serverSession}
                    uiState={uiState}
                    editProfileMethods={editProfileMethods}
                    viewerUser={data?.currentUser}
                  />
                )}
              </div>
            )}
          </div>
        </PageMaxWidth>
      </div>
    </>
  )
}
